import { useState } from 'react'
import Card from '../../components/ui/Card'
import Badge from '../../components/ui/Badge'
import Button from '../../components/ui/Button'
import IntegrationBadge from '../../components/ui/IntegrationBadge'

const mockTransactions = [
  { id: '1', date: '2026-01-26', description: 'SQ *GOSQ.COM REF4829573619', amount: 3245.00, direction: 'credit', status: 'confirmed', category: 'POS Deposit', confidence: 0.95, source: 'square' },
  { id: '2', date: '2026-01-26', description: 'SYSCO CORP ACH PMT', amount: 1523.45, direction: 'debit', status: 'categorized', category: 'Food Vendor', confidence: 0.88, source: 'bank' },
  { id: '3', date: '2026-01-25', description: 'DOORDASH INC PAYOUT', amount: 876.50, direction: 'credit', status: 'new', category: null, confidence: 0, source: 'doordash' },
  { id: '4', date: '2026-01-25', description: 'PEPSICO INC ACH', amount: 432.00, direction: 'debit', status: 'confirmed', category: 'Beverage Vendor', confidence: 0.92, source: 'bank' },
  { id: '5', date: '2026-01-24', description: 'SQ *GOSQ.COM REF9182736450', amount: 2987.00, direction: 'credit', status: 'reconciled', category: 'POS Deposit', confidence: 1.0, source: 'square' },
  { id: '6', date: '2026-01-24', description: 'US FOODS INC PAYMENT', amount: 2156.78, direction: 'debit', status: 'categorized', category: 'Food Vendor', confidence: 0.91, source: 'bank' },
  { id: '7', date: '2026-01-23', description: 'UBER *EATS PAYOUT', amount: 654.25, direction: 'credit', status: 'new', category: null, confidence: 0, source: 'ubereats' },
  { id: '8', date: '2026-01-23', description: 'EDISON ELECTRIC AUTOPAY', amount: 423.50, direction: 'debit', status: 'confirmed', category: 'Utilities', confidence: 0.97, source: 'bank' },
  { id: '9', date: '2026-01-22', description: 'GRUBHUB HOLDINGS ACH', amount: 523.80, direction: 'credit', status: 'new', category: null, confidence: 0, source: 'grubhub' },
]

const categories = [
  'POS Deposit',
  'Food Vendor',
  'Beverage Vendor',
  'Delivery Payout',
  'Utilities',
  'Rent',
  'Wages',
  'Supplies',
]

export default function TransactionList() {
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [filter, setFilter] = useState<string>('all')

  const filteredTransactions = mockTransactions.filter((txn) => {
    if (filter === 'all') return true
    return txn.status === filter
  })

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const selectAll = () => {
    if (selectedIds.length === filteredTransactions.length) {
      setSelectedIds([])
    } else {
      setSelectedIds(filteredTransactions.map((t) => t.id))
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Transactions</h1>
          <p className="text-gray-500 mt-1">Review and categorize your transactions</p>
        </div>
        <div className="flex space-x-3">
          <Button variant="secondary">Export</Button>
          <Button>Run Categorization</Button>
        </div>
      </div>

      {/* Filters */}
      <Card padding="sm">
        <div className="flex items-center justify-between">
          <div className="flex space-x-2">
            {['all', 'new', 'categorized', 'confirmed', 'reconciled'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  filter === status
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
          {selectedIds.length > 0 && (
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-500">{selectedIds.length} selected</span>
              <Button size="sm">Confirm Selected</Button>
            </div>
          )}
        </div>
      </Card>

      {/* Transactions Table */}
      <Card padding="sm">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4">
                <input
                  type="checkbox"
                  checked={selectedIds.length === filteredTransactions.length && filteredTransactions.length > 0}
                  onChange={selectAll}
                  className="rounded border-gray-300"
                />
              </th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Date</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Description</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Source</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Category</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Confidence</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
              <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Amount</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransactions.map((txn) => (
              <tr
                key={txn.id}
                className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
              >
                <td className="py-4 px-4">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(txn.id)}
                    onChange={() => toggleSelect(txn.id)}
                    className="rounded border-gray-300"
                  />
                </td>
                <td className="py-4 px-4 text-sm text-gray-600">{txn.date}</td>
                <td className="py-4 px-4">
                  <span className="font-medium text-gray-900">{txn.description}</span>
                </td>
                <td className="py-4 px-4">
                  <IntegrationBadge platform={txn.source} size="sm" />
                </td>
                <td className="py-4 px-4">
                  {txn.category ? (
                    <select
                      defaultValue={txn.category}
                      className="text-sm border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    >
                      {categories.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  ) : (
                    <select className="text-sm border-yellow-300 bg-yellow-50 rounded-md">
                      <option value="">Select category...</option>
                      {categories.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  )}
                </td>
                <td className="py-4 px-4">
                  {txn.confidence > 0 ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-16 bg-gray-200 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full ${
                            txn.confidence > 0.9
                              ? 'bg-green-500'
                              : txn.confidence > 0.7
                              ? 'bg-yellow-500'
                              : 'bg-red-500'
                          }`}
                          style={{ width: `${txn.confidence * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500">{Math.round(txn.confidence * 100)}%</span>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </td>
                <td className="py-4 px-4">
                  <Badge
                    variant={
                      txn.status === 'reconciled'
                        ? 'success'
                        : txn.status === 'confirmed'
                        ? 'info'
                        : txn.status === 'categorized'
                        ? 'warning'
                        : 'default'
                    }
                  >
                    {txn.status}
                  </Badge>
                </td>
                <td className="py-4 px-4 text-right">
                  <span
                    className={`font-semibold ${
                      txn.direction === 'credit' ? 'text-green-600' : 'text-gray-900'
                    }`}
                  >
                    {txn.direction === 'credit' ? '+' : '-'}$
                    {txn.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}
